/* p7-pdp-tier.js — the PDP tier switcher's moving pill.

   The tiers are <a href> links to sibling PRODUCT PAGES, not tabs — tapping one
   unloads the document (see the comment above .p7-pdp__siblings-list in
   sections/p7-product.liquid, and do not add role="tab"). So the pill cannot
   simply slide: the page it would slide on is gone.

   What happens instead: we animate the pill locally toward the chosen segment,
   then follow the real product URL, and the new page lands with the pill
   already settled behind a skeleton.

   The new page lands at the TOP (owner, 2026-08-09). An earlier version
   recorded scroll depth and restored it on arrival, so you resumed where you
   left off. That was dropped as not worth its cost: the restore had to fire
   before paint to avoid a visible jump, which put it in a race with the
   skeleton, the webfonts and the sticky info column all measuring at once —
   and a tier switch changes the price and the gallery, so the honest thing is
   to show the shopper the top of the product they just switched to. The
   sessionStorage record survives, but now carries only a timestamp: its
   remaining job is telling the arriving page that this was a tier jump and the
   skeleton should be up.

   Progressive enhancement throughout: with JS off, the thumb stays zero-width
   and invisible, and the server-rendered .is-active background on the link is
   the indicator. Nothing here is load-bearing for knowing which tier you are on.
*/
(function () {
  'use strict';

  var KEY = 'p7:tier-nav';
  var MAX_AGE_MS = 15000;   // a stale record must never yank a later visit
  var LEAVE_MS = 170;
  // Floor, not duration. The skeleton is held until the gallery image has
  // decoded; this only stops a cache hit from flashing it for one frame.
  var ARRIVE_MIN_MS = 180;

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function place(thumb, left, width) {
    thumb.style.left = left + 'px';
    thumb.style.width = width + 'px';
  }

  /* Hold the arrival skeleton until the page it is standing in for is actually
     ready to be seen.

     This used to be a flat 220ms from DOMContentLoaded, which is a guess about
     how long a page takes to paint rather than a measurement of it. On anything
     slower than a warm cache the shimmer lifted while the gallery was still
     blank, so the shopper watched the page assemble itself — the exact flicker
     the skeleton exists to hide.

     The gallery image is the right thing to wait on: it is the largest element
     and the last to settle, so once it has decoded the page is visually done.
     `complete && naturalWidth > 0` covers the cache hit, where `load` has
     already fired and would never fire again. `error` releases too — a broken
     image is not a reason to hold someone behind a shimmer.

     The inline boot script in p7-product.liquid carries a 4000ms cap in case
     this file never arrives; nothing here is load-bearing for that. */
  function releaseSkeleton() {
    var done = false;
    function release() {
      if (done) return;
      done = true;
      document.documentElement.classList.remove('p7-pdp-tier-boot');
    }
    // A floor, so a cached page does not flash the skeleton for a single frame.
    function releaseAfterFloor() {
      window.setTimeout(release, ARRIVE_MIN_MS);
    }

    var img = document.querySelector('.p7-pdp__main img');
    if (!img) { release(); return; }
    if (img.complete && img.naturalWidth > 0) { releaseAfterFloor(); return; }
    img.addEventListener('load', releaseAfterFloor, { once: true });
    img.addEventListener('error', release, { once: true });
  }

  function init() {
    var track = document.querySelector('[data-tier-track]');
    if (!track) return;
    var thumb = track.querySelector('[data-tier-thumb]');
    var tabs = Array.prototype.slice.call(track.querySelectorAll('.p7-pdp__sibling'));
    if (!thumb || tabs.length < 2) return;

    var activeIndex = tabs.findIndex(function (t) {
      return t.classList.contains('is-active');
    });
    // No active segment means this product is not one of its own siblings —
    // possible if the metafield list is stale. Leave the control alone entirely
    // rather than pointing the thumb at an arbitrary tier.
    if (activeIndex === -1) return;

    var active = tabs[activeIndex];

    function settle() {
      place(thumb, active.offsetLeft, active.offsetWidth);
    }

    /* ---- 1. arriving: restore depth, then place the pill ---- */
    var record = null;
    try {
      var raw = sessionStorage.getItem(KEY);
      if (raw) record = JSON.parse(raw);
    } catch (err) {
      record = null;
    }
    // One-shot. Clear before using it, so a failure below cannot leave a record
    // that replays on every subsequent load.
    try { sessionStorage.removeItem(KEY); } catch (err) { /* private mode */ }

    // Only a timestamp is required now. record.y is deliberately NOT read:
    // arrivals land at the top, and an older record that still carries a y is
    // simply ignored rather than being treated as malformed.
    var fresh = record &&
      typeof record.t === 'number' &&
      (Date.now() - record.t) < MAX_AGE_MS;

    track.classList.add('has-thumb');

    thumb.style.transition = 'none';
    settle();
    if (fresh) releaseSkeleton();

    /* ---- 2. leaving: animate locally, then follow the product link ---- */
    tabs.forEach(function (tab, index) {
      tab.addEventListener('click', function (event) {
        if (index === activeIndex) return;          // already here
        if (
          event.defaultPrevented ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          (event.button != null && event.button !== 0)
        ) {
          return;
        }

        try {
          // Timestamp only — this marks the next load as a tier jump so it
          // shows the skeleton. Scroll depth is no longer recorded.
          sessionStorage.setItem(KEY, JSON.stringify({ t: Date.now() }));
        } catch (err) { /* private mode — navigation still works, just no skeleton */ }

        if (reducedMotion()) return;

        event.preventDefault();
        track.dataset.tierAnimating = '1';
        thumb.style.transition =
          'left ' + LEAVE_MS + 'ms cubic-bezier(.2,.8,.2,1), ' +
          'width ' + LEAVE_MS + 'ms cubic-bezier(.2,.8,.2,1)';
        place(thumb, tab.offsetLeft, tab.offsetWidth);

        window.setTimeout(function () {
          document.documentElement.classList.add('p7-pdp-tier-boot');
          window.location.href = tab.href;
        }, LEAVE_MS);
      });
    });

    /* ---- 3. keep the pill on its segment when the track resizes ---- */
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        thumb.style.transition = 'none';
        settle();
      }, 100);
    });

    // Late webfonts change label widths and therefore segment widths.
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(function () {
        if (!track.dataset.tierAnimating) settle();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
